/**
 * Who is reading the plan, and the token that proves it.
 *
 * The API refuses every call without an Entra token and a `dispatcher` or
 * `admin` role (spec section 3, coding standards 11.4 and 11.5). The policy of
 * getting that token lives in `@depicomnlbv/platform-session` and not here.
 * This file is the three settings and the instance, which is all a host owns;
 * the policy is `@depicomnlbv/platform-session`'s.
 */
import { PublicClientApplication } from "@azure/msal-browser";
import {
  ConfigurationError,
  createSession,
  msalConfiguration,
} from "@depicomnlbv/platform-session";

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? "";
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? "";
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE ?? "";

// No instance without a registration to point it at. In development there is
// usually none: the screen runs signed out, and the API refuses its calls
// unless fixture mode answers them instead (`api/client.ts`).
const msal =
  tenantId && clientId
    ? new PublicClientApplication(msalConfiguration({ tenantId, clientId }))
    : null;

function build(requireConfiguration: boolean) {
  return createSession({
    tenantId,
    clientId,
    apiScope,
    msal,
    requireConfiguration,
    // Every screen here has an
    // address, and a deep link to a day and a trip has to survive the round
    // trip to Entra. The session hands the path back before the router mounts
    // (`main.tsx` awaits `start()` first), so replacing the address is enough.
    onRestore: (path) => window.history.replaceState(null, "", path),
  });
}

/**
 * The one sentence a deployment that cannot sign anyone in shows.
 *
 * Named by the settings this file reads rather than by the fields the session
 * package reports missing: its list says `clientId` and `msal`, and a person
 * fixing a deployment is looking for the name in `.env.example`.
 */
export function notConfiguredSentence(missing: string[]): string {
  const names =
    missing.length > 1
      ? `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`
      : (missing[0] ?? "its Entra settings");
  return `This deployment cannot sign anyone in, because it was built without ${names}.`;
}

const SETTINGS: [string, string][] = [
  ["VITE_ENTRA_TENANT_ID", tenantId],
  ["VITE_ENTRA_CLIENT_ID", clientId],
  ["VITE_ENTRA_API_SCOPE", apiScope],
];

let failure: string | null = null;
let built;
try {
  // A production bundle with a setting missing throws here rather than
  // rendering a planner that asks nobody who they are.
  built = build(import.meta.env.PROD);
} catch (error) {
  // It fails closed either way. This is only so that it fails closed with a
  // sentence on the screen rather than a blank page and a line in a console
  // nobody deploying is watching. Anything other than the configuration
  // refusal is not expected here, and is still a refusal to start.
  failure =
    error instanceof ConfigurationError
      ? notConfiguredSentence(SETTINGS.filter(([, value]) => !value).map(([name]) => name))
      : `This deployment cannot sign anyone in: ${error instanceof Error ? error.message : String(error)}`;
  built = build(false);
}

export const session = built;

/** Set when this bundle was built for a deployment and cannot sign anybody
 *  in. The screen says so in this one sentence and offers nothing else. */
export const configurationFailure = failure;

/** The name to show in the bar: the account's own name, then its sign-in name. */
export function signedInAs(): string | null {
  const account = session.account();
  if (!account) return null;
  return account.name ?? account.username ?? null;
}
